import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  FiTrendingUp,
  FiBriefcase,
  FiCalendar,
} from "react-icons/fi";
import { LuIndianRupee } from "react-icons/lu";
import Swal from "sweetalert2";
import DynamicTable from "../Common/DynamicTable";
import { expenseColumns } from "./ExpensesColums";
import ExpensesCreate from "./ExpensesCreate";
import ExpenseDrawer from "./ExpensesDrawer";
import api from "../../api/axios";
import DateFilter from "../Common/DateFilter";
import { buildDateFilterParams } from "../../utils/dateFilterUtils";

import "../../assets/css/Expenses/ExpensesList.css";
import "../../assets/css/Common/stats_cards.css";

const DEFAULT_CARDS = {
  total_expense: 0,
  lead_expense: 0,
  deal_expense: 0,
};

export default function ExpensesList() {
  const [expenses, setExpenses] = useState([]);
  const [cards, setCards] = useState(DEFAULT_CARDS);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");

  const [selectedExpense, setSelectedExpense] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const requestIdRef = useRef(0);

  const fetchData = async (filter = dateFilter) => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = buildDateFilterParams(filter);
      const hasParams = Object.keys(params).length > 0;
      const [expensesRes, cardsRes] = await Promise.all([
        hasParams ? api.get("/expense", { params }) : api.get("/expense"),
        hasParams ? api.get("/expense/cards", { params }) : api.get("/expense/cards"),
      ]);

      if (currentRequestId !== requestIdRef.current) return;

      setExpenses(Array.isArray(expensesRes.data) ? expensesRes.data : []);
      setCards(cardsRes.data || DEFAULT_CARDS);
    } catch (error) {
      if (currentRequestId !== requestIdRef.current) return;
      console.error("Failed to fetch expenses:", error);
      setExpenses([]);
      setCards(DEFAULT_CARDS);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchExpenses = (filter) => fetchData(filter);
  const fetchCards = (filter) => fetchData(filter);

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    fetchData(newFilter);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ---------------- ROW CLICK open expense----------------
  const openExpense = async (id) => {
    try {
      setLoadingExpenses(true);

      const response = await api.get(`/expense/${id}`);

      setSelectedExpense(response.data);
      setOpenDrawer(true);

    } catch (error) {
      console.error("Error fetching expense:", error);

      Swal.fire({
        icon: "error",
        title: "Failed to load expense details",
      });
    } finally {
      setLoadingExpenses(false);
    }
  };


  //bulk delete 

  const handleBulkDelete = async () => {

    if (!selectedIds.length) {

      await Swal.fire({
        icon: "warning",
        title: "Select expense first"
      });

      return;
    }

    const res = await Swal.fire({
      title: `Delete ${selectedIds.length} expenses?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });

    if (!res.isConfirmed) return;

    try {
      await api.post("/expense/bulk-delete", {
        expense_ids: selectedIds, // 👈 IMPORTANT FIX (matches backend)
      });

      await Swal.fire({
        icon: "success",
        title: "Deleted Successfully",
        timer: 1200,
        showConfirmButton: false,
      });

      setSelectedIds([]);
      fetchExpenses(); // refresh list
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Bulk Delete Failed",
      });
    }
  };


  // ---------------- SELECT HANDLERS ----------------
  const handleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  }, []);

  // ---------------- FILTERED DATA ----------------
  const filteredData = useMemo(() => {

    const lowerSearch = (search || "").toLowerCase().trim();

    return expenses.filter((expense) => {
      const title =
        expense.deal?.title ||
        expense.lead?.title ||
        "";

      const matchesSearch =
        !lowerSearch ||
        title.toLowerCase().includes(lowerSearch) ||
        (expense.expense_type || "").toLowerCase().includes(lowerSearch) ||
        (expense.notes || "").toLowerCase().includes(lowerSearch) ||
        (expense.remarks || "").toLowerCase().includes(lowerSearch) ||
        (expense.organization?.name || "").toLowerCase().includes(lowerSearch) ||
        (expense.created_by_name || "").toLowerCase().includes(lowerSearch) ||
        String(expense.amount || "").toLowerCase().includes(lowerSearch) ||
        String(expense.id || "").includes(lowerSearch);

      const matchesType =
        typeFilter
          ? expense.expense_type === typeFilter
          : true;

      return matchesSearch && matchesType;

    });

  }, [expenses, search, typeFilter]);

  // ---------------- EXPENSE TYPES ----------------
  const expenseTypes = useMemo(() => {
    return [...new Set(expenses.map((x) => x.expense_type).filter(Boolean))];
  }, [expenses]);

  // ---------------- STATS ----------------
  const stats = useMemo(() => {
    const totalExpenses = expenses.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0
    );

    return {
      totalExpenses,
      leadExpenses: expenses.filter((e) => e.lead_id).length,
      dealExpenses: expenses.filter((e) => e.deal_id).length,
      selected: selectedIds.length,
    };
  }, [expenses, selectedIds]);


  //SORTING

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const getSortValue = (expense, key) => {
    switch (key) {
      case "title":
        return expense.deal?.title || expense.lead?.title || "";

      case "related_to":
        return expense.deal ? "Deal" : expense.lead ? "Lead" : "";

      case "organization":
        return expense.organization?.name || "";

      case "amount":
        return Number(expense.amount || 0);

      case "expense_date":
        return expense.expense_date
          ? new Date(expense.expense_date).getTime()
          : 0;

      case "created_by":
        return expense.created_by?.name || "";

      default:
        return expense[key] || "";
    }
  };




const sortedData = useMemo(() => {

  const data = [...filteredData];

  if (!sortConfig.key)
    return data;

  return data.sort((a,b)=>{

    const aVal =
      getSortValue(a, sortConfig.key);

    const bVal =
      getSortValue(b, sortConfig.key);


    if (typeof aVal === "number") {

      return sortConfig.direction === "asc"
        ? aVal - bVal
        : bVal - aVal;

    }


    return sortConfig.direction === "asc"
      ? String(aVal)
          .localeCompare(String(bVal))
      : String(bVal)
          .localeCompare(String(aVal));

  });

},[
 filteredData,
 sortConfig
]);


  const handleSortChange = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };




  // AFTER sortedData
  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.length === sortedData.length
        ? []
        : sortedData.map((x) => x.id)
    );
  }, [sortedData]);





  const user = JSON.parse(sessionStorage.getItem("user"));
  const canDelete =
    user?.role !== "Sales Executive" &&
    user?.role !== "Sales Manager";







  // ---------------- UI ----------------
  return (
    <div className="main-wrapper">

      {/* HEADER */}
      <div className="page-header">
        <h2>Expenses</h2>
        <DateFilter value={dateFilter} onChange={handleDateFilterChange} />
      </div>

      {/* STATS */}
      <div className={`stats-grid stats-grid-4${loading ? " refreshing" : ""}`}>

        <div className="stat-card">
          <div className="stat-icon blue">
            <LuIndianRupee />
          </div>
          <div>

            <p>Total Expenses</p>
            <h2>{cards?.total_expense ?? 0}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <FiTrendingUp />
          </div>
          <div>

            <p>Lead Expenses</p>
            <h2>{cards?.lead_expense ?? 0}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <FiBriefcase />
          </div>
          <div>

            <p>Deal Expenses</p>
            <h2>{cards?.deal_expense ?? 0}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon gold">
            <FiCalendar />
          </div>
          <div>

            <p>Selected</p>
            <h2>{stats.selected}</h2>
          </div>
        </div>

      </div>

      {/* TABLE */}
      <div className="table-card">

        <DynamicTable
          data={sortedData}
          columns={expenseColumns({
            selectedIds,
            handleSelect,
            handleSelectAll,
            onRowClick: openExpense,
            totalRows: sortedData.length,
          })}
          onRowClick={(id) => openExpense(id)}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search expenses..."
          selectedIds={selectedIds}
          selectedCount={selectedIds.length}
          canDelete={canDelete}
          storageKey="expenses_columns"
          addButtonText="Log Expense"
          filters={[
            {
              name: "type",
              value: typeFilter,
              onChange: setTypeFilter,
              options: expenseTypes,
              placeholder: "All Types",
            },
          ]}
          onBulkDelete={handleBulkDelete}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
        />

      </div>

      {/* DRAWER */}
      {openDrawer && selectedExpense && (
        <ExpenseDrawer
          expense={selectedExpense}
          isOpen={openDrawer}
          onClose={() => {
            setOpenDrawer(false);
            setSelectedExpense(null);
          }}
        />
      )}

    </div>
  );
}